import React, { useState, useEffect } from 'react';
import { carteraService } from '../services/api';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';

const OverpaymentsPage = () => {
    const [loading, setLoading] = useState(false);
    const [overpayments, setOverpayments] = useState([]);
    const [filters, setFilters] = useState({
        from: '',
        to: ''
    });

    // Estado para modal de devolución
    const [refundModal, setRefundModal] = useState({
        isOpen: false,
        loading: false,
        order: null,
        amount: '',
        payment_method: 'transferencia',
        notes: ''
    });

    const openRefundModal = (item) => {
        setRefundModal({
            isOpen: true,
            loading: false,
            order: item,
            amount: item.sobrepago, // Por defecto el total
            payment_method: 'transferencia',
            notes: ''
        });
    };

    const handleRefundSubmit = async (e) => {
        e.preventDefault();
        if (!refundModal.amount || parseFloat(refundModal.amount) <= 0) {
            toast.error('El monto debe ser mayor a 0');
            return;
        }

        setRefundModal(prev => ({ ...prev, loading: true }));
        try {
            await carteraService.refundOverpayment({
                order_number: refundModal.order.order_number,
                amount: refundModal.amount,
                payment_method: refundModal.payment_method,
                notes: refundModal.notes
            });
            toast.success('Devolución registrada exitosamente');
            setRefundModal({ isOpen: false, order: null, amount: '', notes: '', loading: false });
            loadOverpayments();
        } catch (error) {
            console.error(error);
            toast.error('Error registrando devolución');
            setRefundModal(prev => ({ ...prev, loading: false }));
        }
    };

    const loadOverpayments = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.from) params.from = filters.from;
            if (filters.to) params.to = filters.to;

            const res = await carteraService.getOverpayments(params);
            const data = res?.data || [];
            setOverpayments(data);
        } catch (e) {
            toast.error('Error cargando sobrepagos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOverpayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fmt = (val) => {
        const n = Number(val || 0);
        return n.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const totalSobrepago = overpayments.reduce((sum, item) => sum + Number(item.sobrepago || 0), 0);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                                <Icons.DollarSign className="w-8 h-8 text-orange-600 mr-3" />
                                Sobrepagos Pendientes de Devolución
                            </h1>
                            <p className="mt-2 text-sm text-gray-600">
                                Gestiona los pagos donde el cliente pagó más del total del pedido
                            </p>
                        </div>
                        <button
                            onClick={loadOverpayments}
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow disabled:opacity-50"
                        >
                            <Icons.RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                            <span>Actualizar</span>
                        </button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
                            <input
                                type="date"
                                value={filters.from}
                                onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
                            <input
                                type="date"
                                value={filters.to}
                                onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="md:col-span-2 flex items-end space-x-3">
                            <button
                                onClick={loadOverpayments}
                                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-medium"
                            >
                                Aplicar Filtros
                            </button>
                            <button
                                onClick={() => {
                                    setFilters({ from: '', to: '' });
                                    setTimeout(loadOverpayments, 100);
                                }}
                                className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg"
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Resumen */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-orange-100 text-sm font-medium">Total Sobrepagos</p>
                                <p className="text-3xl font-bold mt-2">${fmt(totalSobrepago)}</p>
                            </div>
                            <div className="bg-white/20 p-3 rounded-lg">
                                <Icons.TrendingUp className="w-8 h-8" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm font-medium">Pedidos con Sobrepago</p>
                                <p className="text-3xl font-bold mt-2">{overpayments.length}</p>
                            </div>
                            <div className="bg-white/20 p-3 rounded-lg">
                                <Icons.FileText className="w-8 h-8" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-sm font-medium">Promedio por Pedido</p>
                                <p className="text-3xl font-bold mt-2">
                                    ${fmt(overpayments.length > 0 ? totalSobrepago / overpayments.length : 0)}
                                </p>
                            </div>
                            <div className="bg-white/20 p-3 rounded-lg">
                                <Icons.Calculator className="w-8 h-8" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabla */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Pedido
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cliente
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Teléfono
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Monto Pagado
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total Pedido
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Sobrepago
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo Pago
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Registrado Por
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center">
                                                <Icons.Loader className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                                                <span className="text-gray-600">Cargando sobrepagos...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : overpayments.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center">
                                                <Icons.CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                                                <p className="text-lg font-medium text-gray-900">No hay sobrepagos registrados</p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {filters.from || filters.to ? 'Intenta cambiar los filtros' : 'Todos los pagos están correctos'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    overpayments.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {item.fecha || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">
                                                {item.order_number}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {item.customer_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {item.customer_phone || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                                ${fmt(item.monto_pagado)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                                                ${fmt(item.total_pedido)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-orange-100 text-orange-700">
                                                    ${fmt(item.sobrepago)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${item.tipo_pago === 'efectivo'
                                                    ? 'bg-green-100 text-green-800'
                                                    : item.tipo_pago === 'transferencia'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {item.tipo_pago === 'efectivo' ? 'Efectivo' : item.tipo_pago === 'transferencia' ? 'Transferencia' : item.tipo_pago}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {item.registrado_por || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => openRefundModal(item)}
                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    <Icons.RotateCcw className="w-3 h-3 mr-1" />
                                                    Devolver
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Nota informativa */}
                {overpayments.length > 0 && (
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <Icons.Info className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">Nota Importante</h3>
                                <div className="mt-2 text-sm text-blue-700">
                                    <p>
                                        Estos montos corresponden a pagos donde el cliente pagó más del total del pedido.
                                        Recomendaciones:
                                    </p>
                                    <ul className="list-disc list-inside mt-2 space-y-1">
                                        <li>Verificar con el cliente si fue intencional (abono a otro pedido)</li>
                                        <li>Si fue error, coordinar la devolución del monto</li>
                                        <li>Documentar la gestión realizada en las observaciones del pedido</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Devolución */}
                {refundModal.isOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setRefundModal(p => ({ ...p, isOpen: false }))}></div>

                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <form onSubmit={handleRefundSubmit}>
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                                <Icons.RotateCcw className="h-6 w-6 text-blue-600" aria-hidden="true" />
                                            </div>
                                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                    Registrar Devolución de Dinero
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500 mb-4">
                                                        Registra la salida de dinero para corregir el sobrepago del pedido <span className="font-bold">#{refundModal.order?.order_number}</span>.
                                                    </p>

                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Monto a Devolver</label>
                                                            <div className="mt-1 relative rounded-md shadow-sm">
                                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                    <span className="text-gray-500 sm:text-sm">$</span>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    required
                                                                    min="1"
                                                                    max={refundModal.order?.sobrepago}
                                                                    value={refundModal.amount}
                                                                    onChange={(e) => setRefundModal(p => ({ ...p, amount: e.target.value }))}
                                                                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                                                                    placeholder="0.00"
                                                                />
                                                            </div>
                                                            <p className="mt-1 text-xs text-gray-500">
                                                                Máximo disponible: ${fmt(refundModal.order?.sobrepago)}
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Medio de Devolución</label>
                                                            <select
                                                                value={refundModal.payment_method}
                                                                onChange={(e) => setRefundModal(p => ({ ...p, payment_method: e.target.value }))}
                                                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                                            >
                                                                <option value="transferencia">Transferencia Bancaria</option>
                                                                <option value="efectivo">Efectivo</option>
                                                                <option value="otro">Otro</option>
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Notas / Evidencia</label>
                                                            <textarea
                                                                rows="2"
                                                                value={refundModal.notes}
                                                                onChange={(e) => setRefundModal(p => ({ ...p, notes: e.target.value }))}
                                                                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 mt-1 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                placeholder="Opcional: motivo o referencia de la transacción"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="submit"
                                            disabled={refundModal.loading}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                        >
                                            {refundModal.loading ? 'Registrando...' : 'Confirmar Devolución'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={refundModal.loading}
                                            onClick={() => setRefundModal(p => ({ ...p, isOpen: false }))}
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default OverpaymentsPage;
